const {
  MONTHS_IN_RESPONSE,
  TREND_LOOKBACK_MONTHS,
  checkForRequirements,
  buildAnalyticsBaseContext,
  buildIndicatorQueryContext,
  buildMonthlyIndicatorDateWindow,
  buildMonthlyIndicatorQueryContext,
  appendScopeAndProductFilters,
  getLocationTableGrouping,
  aggregateNationalLocationRows,
  getCurrentUtcMonthKey,
  calculateDeltaPct,
  roundTo2,
  toNumberOrNull,
  divideOrNull,
  roundOrNull
} = require('../utils')

// A sale missing either price counts towards nothing but revenue, which keeps every sale
// with a known sell price in the revenue figure. Gross profit therefore reads high while
// costs are missing, and the missing prices banner is what flags the figures as incomplete.
const PRICED_ROW_CONDITION = 'unit_price_at_sale IS NOT NULL'
const PRICED_AND_COSTED_ROW_CONDITION = 'unit_price_at_sale IS NOT NULL AND unit_cost_at_sale IS NOT NULL'

const GROSS_PROFIT_LOCATION_SUM_FIELDS = [
  'total_rows',
  'costed_rows',
  'missing_price_only_rows',
  'missing_cost_only_rows',
  'missing_price_and_cost_rows',
  'units_sold_total',
  'revenue_total',
  'cogs_total',
  'gross_profit_total',
  'current_month_gross_profit_total',
  'previous_3_months_gross_profit_total'
]

function calculateGrossMarginPercent (revenueTotal, cogsTotal) {
  if (revenueTotal === 0) {
    return null
  }
  return roundTo2(((revenueTotal - cogsTotal) / revenueTotal) * 100)
}

// The rolling average is the margin of the prior 3 months taken together, not the mean of
// their 3 margins, so bigger months weigh more
function calculateMonthlyGrossMarginTrend (monthlyRows, rowIndex, currentMonthKey) {
  if (monthlyRows[rowIndex].month === currentMonthKey) {
    return null
  }
  if (rowIndex < TREND_LOOKBACK_MONTHS) {
    return null
  }
  const previousMonths = monthlyRows.slice(rowIndex - TREND_LOOKBACK_MONTHS, rowIndex)
  const previousRevenueTotal = previousMonths.reduce((sum, row) => sum + row.revenueTotal, 0)
  const previousCogsTotal = previousMonths.reduce((sum, row) => sum + row.cogsTotal, 0)
  return calculateGrossMarginPercent(previousRevenueTotal, previousCogsTotal)
}

function mapMonthlyGrossMarginRows (queryRows, currentMonthKey) {
  const monthlyRows = queryRows.map((row) => ({
    month: row.month,
    revenueTotal: Number(row.revenue_total || 0),
    cogsTotal: Number(row.cogs_total || 0)
  }))

  return monthlyRows.map((row, index) => ({
    month: row.month,
    revenueTotal: row.revenueTotal,
    cogsTotal: row.cogsTotal,
    grossProfitTotal: roundTo2(row.revenueTotal - row.cogsTotal),
    grossMarginPercent: calculateGrossMarginPercent(row.revenueTotal, row.cogsTotal),
    trend: calculateMonthlyGrossMarginTrend(monthlyRows, index, currentMonthKey)
  }))
}

async function getGrossProfitSummary ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds }) {
  checkForRequirements(pgConnection, location)
  const { fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
  const { currentMonthDays, previous3MonthsPeriodDays, params, whereClauses } = buildIndicatorQueryContext({
    organisationId,
    fromDateUtc,
    toDateUtc,
    toDateExclusiveUtc,
    locationScope,
    productSku,
    serviceIds
  })

  const query = `
      WITH filtered AS (
        SELECT
          si.time_of_sale,
          si.units_sold::numeric AS units_sold,
          si.unit_price_at_sale,
          si.unit_cost_at_sale
        FROM analytics.sales_indicators si
        INNER JOIN avocado.data_location dl
          ON dl.uuid = si.location_id
        WHERE organisation_id = $1
          AND si.time_of_sale >= $2::timestamptz
          AND si.time_of_sale < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        COUNT(*) FILTER (WHERE time_of_sale >= $6::timestamptz AND time_of_sale < $5::timestamptz)::int AS total_rows,
        COUNT(*) FILTER (WHERE ${PRICED_AND_COSTED_ROW_CONDITION} AND time_of_sale >= $6::timestamptz AND time_of_sale < $5::timestamptz)::int AS costed_rows,
        COUNT(*) FILTER (WHERE unit_price_at_sale IS NULL AND unit_cost_at_sale IS NOT NULL AND time_of_sale >= $6::timestamptz AND time_of_sale < $5::timestamptz)::int AS missing_price_only_rows,
        COUNT(*) FILTER (WHERE unit_cost_at_sale IS NULL AND unit_price_at_sale IS NOT NULL AND time_of_sale >= $6::timestamptz AND time_of_sale < $5::timestamptz)::int AS missing_cost_only_rows,
        COUNT(*) FILTER (WHERE unit_price_at_sale IS NULL AND unit_cost_at_sale IS NULL AND time_of_sale >= $6::timestamptz AND time_of_sale < $5::timestamptz)::int AS missing_price_and_cost_rows,
        COALESCE(SUM(units_sold) FILTER (WHERE time_of_sale >= $6::timestamptz AND time_of_sale < $5::timestamptz), 0)::numeric AS units_sold_total,
        COALESCE(
          SUM(units_sold * unit_price_at_sale) FILTER (
            WHERE ${PRICED_ROW_CONDITION}
              AND time_of_sale >= $6::timestamptz
              AND time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS revenue_total,
        COALESCE(
          SUM(units_sold * unit_cost_at_sale) FILTER (
            WHERE ${PRICED_AND_COSTED_ROW_CONDITION}
              AND time_of_sale >= $6::timestamptz
              AND time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS cogs_total,
        COALESCE(
          SUM(units_sold * unit_price_at_sale) FILTER (
            WHERE ${PRICED_ROW_CONDITION}
              AND time_of_sale >= $4::timestamptz
              AND time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS current_month_revenue_total,
        COALESCE(
          SUM(units_sold * unit_cost_at_sale) FILTER (
            WHERE ${PRICED_AND_COSTED_ROW_CONDITION}
              AND time_of_sale >= $4::timestamptz
              AND time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS current_month_cogs_total,
        COALESCE(
          SUM(units_sold * unit_price_at_sale) FILTER (
            WHERE ${PRICED_ROW_CONDITION}
              AND time_of_sale >= $2::timestamptz
              AND time_of_sale < $4::timestamptz
          ),
          0
        )::numeric AS previous_3_months_revenue_total,
        COALESCE(
          SUM(units_sold * unit_cost_at_sale) FILTER (
            WHERE ${PRICED_AND_COSTED_ROW_CONDITION}
              AND time_of_sale >= $2::timestamptz
              AND time_of_sale < $4::timestamptz
          ),
          0
        )::numeric AS previous_3_months_cogs_total
      FROM filtered
    `

  const { rows } = await pgConnection.query(query, params)
  const row = rows[0] || {}
  const costedRows = Number(row.costed_rows || 0)
  const revenueTotal = Number(row.revenue_total || 0)
  const cogsTotal = Number(row.cogs_total || 0)
  const currentAverageDailyGrossProfit = (Number(row.current_month_revenue_total || 0) - Number(row.current_month_cogs_total || 0)) / currentMonthDays
  const previous3MonthsAverageDailyGrossProfit = (Number(row.previous_3_months_revenue_total || 0) - Number(row.previous_3_months_cogs_total || 0)) / previous3MonthsPeriodDays
  const grossProfitDeltaPercent = calculateDeltaPct(currentAverageDailyGrossProfit, previous3MonthsAverageDailyGrossProfit)
  const grossProfitTotal = roundTo2(revenueTotal - cogsTotal)
  const grossMarginPercent = revenueTotal === 0
    ? null
    : roundTo2((grossProfitTotal / revenueTotal) * 100)

  return {
    fromDate,
    toDate,
    organisationId,
    location: locationScope.location,
    productSku: productSku || null,
    totalRows: Number(row.total_rows || 0),
    costedRows: costedRows,
    missingPriceOnlyRows: Number(row.missing_price_only_rows || 0),
    missingCostOnlyRows: Number(row.missing_cost_only_rows || 0),
    missingPriceAndCostRows: Number(row.missing_price_and_cost_rows || 0),
    unitsSoldTotal: Number(row.units_sold_total || 0),
    revenueTotal: revenueTotal,
    cogsTotal: cogsTotal,
    grossProfitTotal: grossProfitTotal,
    grossMarginPercent: grossMarginPercent,
    previous3MonthsAverageDailyGrossProfit: roundTo2(previous3MonthsAverageDailyGrossProfit),
    currentAverageDailyGrossProfit: roundTo2(currentAverageDailyGrossProfit),
    grossProfitDeltaPercent: grossProfitDeltaPercent
  }
}

async function getGrossProfitByProductTable ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds }) {
  checkForRequirements(pgConnection, location)
  const { fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
  const { currentMonthDays, previous3MonthsPeriodDays, params, whereClauses } = buildIndicatorQueryContext({
    organisationId,
    fromDateUtc,
    toDateUtc,
    toDateExclusiveUtc,
    locationScope,
    productSku,
    serviceIds
  })

  const costedRowsInWindow = `COUNT(*) FILTER (
          WHERE ${PRICED_AND_COSTED_ROW_CONDITION}
            AND f.time_of_sale >= $6::timestamptz
            AND f.time_of_sale < $5::timestamptz
        )`

  const query = `
      WITH filtered AS (
        SELECT
          si.product_sku_id,
          si.time_of_sale,
          si.units_sold::numeric AS units_sold,
          si.unit_price_at_sale,
          si.unit_cost_at_sale
        FROM analytics.sales_indicators si
        INNER JOIN avocado.data_location dl
          ON dl.uuid = si.location_id
        WHERE organisation_id = $1
          AND si.time_of_sale >= $2::timestamptz
          AND si.time_of_sale < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        f.product_sku_id AS product_sku,
        COALESCE(ps.display_name, f.product_sku_id) AS product_name,
        COUNT(*) FILTER (
          WHERE f.time_of_sale >= $6::timestamptz
            AND f.time_of_sale < $5::timestamptz
        )::int AS total_rows,
        ${costedRowsInWindow}::int AS costed_rows,
        COUNT(*) FILTER (
          WHERE f.unit_price_at_sale IS NULL
            AND f.unit_cost_at_sale IS NOT NULL
            AND f.time_of_sale >= $6::timestamptz
            AND f.time_of_sale < $5::timestamptz
        )::int AS missing_price_only_rows,
        COUNT(*) FILTER (
          WHERE f.unit_cost_at_sale IS NULL
            AND f.unit_price_at_sale IS NOT NULL
            AND f.time_of_sale >= $6::timestamptz
            AND f.time_of_sale < $5::timestamptz
        )::int AS missing_cost_only_rows,
        COUNT(*) FILTER (
          WHERE f.unit_price_at_sale IS NULL
            AND f.unit_cost_at_sale IS NULL
            AND f.time_of_sale >= $6::timestamptz
            AND f.time_of_sale < $5::timestamptz
        )::int AS missing_price_and_cost_rows,
        COALESCE(
          SUM(f.units_sold) FILTER (
            WHERE f.units_sold IS NOT NULL
              AND f.time_of_sale >= $6::timestamptz
              AND f.time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS units_sold_total,
        COALESCE(
          SUM(f.units_sold * f.unit_price_at_sale) FILTER (
            WHERE ${PRICED_ROW_CONDITION}
              AND f.time_of_sale >= $6::timestamptz
              AND f.time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS revenue_total,
        CASE WHEN ${costedRowsInWindow} = 0 THEN NULL ELSE COALESCE(
          SUM(f.units_sold * f.unit_cost_at_sale) FILTER (
            WHERE ${PRICED_AND_COSTED_ROW_CONDITION}
              AND f.time_of_sale >= $6::timestamptz
              AND f.time_of_sale < $5::timestamptz
          ),
          0
        ) END::numeric AS cogs_total,
        CASE WHEN ${costedRowsInWindow} = 0 THEN NULL ELSE
          COALESCE(
            SUM(f.units_sold * f.unit_price_at_sale) FILTER (
              WHERE ${PRICED_ROW_CONDITION}
                AND f.time_of_sale >= $6::timestamptz
                AND f.time_of_sale < $5::timestamptz
            ),
            0
          )
          - COALESCE(
            SUM(f.units_sold * f.unit_cost_at_sale) FILTER (
              WHERE ${PRICED_AND_COSTED_ROW_CONDITION}
                AND f.time_of_sale >= $6::timestamptz
                AND f.time_of_sale < $5::timestamptz
            ),
            0
          )
        END::numeric AS gross_profit_total,
        CASE WHEN ${costedRowsInWindow} = 0 THEN NULL ELSE
          COALESCE(
            SUM(f.units_sold * f.unit_price_at_sale) FILTER (
              WHERE ${PRICED_ROW_CONDITION}
                AND f.time_of_sale >= $4::timestamptz
                AND f.time_of_sale < $5::timestamptz
            ),
            0
          )
          - COALESCE(
            SUM(f.units_sold * f.unit_cost_at_sale) FILTER (
              WHERE ${PRICED_AND_COSTED_ROW_CONDITION}
                AND f.time_of_sale >= $4::timestamptz
                AND f.time_of_sale < $5::timestamptz
            ),
            0
          )
        END::numeric AS current_month_gross_profit_total,
        CASE WHEN ${costedRowsInWindow} = 0 THEN NULL ELSE
          COALESCE(
            SUM(f.units_sold * f.unit_price_at_sale) FILTER (
              WHERE ${PRICED_ROW_CONDITION}
                AND f.time_of_sale >= $2::timestamptz
                AND f.time_of_sale < $4::timestamptz
            ),
            0
          )
          - COALESCE(
            SUM(f.units_sold * f.unit_cost_at_sale) FILTER (
              WHERE ${PRICED_AND_COSTED_ROW_CONDITION}
                AND f.time_of_sale >= $2::timestamptz
                AND f.time_of_sale < $4::timestamptz
            ),
            0
          )
        END::numeric AS previous_3_months_gross_profit_total
      FROM filtered f
      LEFT JOIN avocado.data_productsku ps
        ON ps.old_sku_id = f.product_sku_id
      WHERE f.product_sku_id IS NOT NULL
      GROUP BY f.product_sku_id, ps.display_name
      HAVING COALESCE(
        SUM(f.units_sold) FILTER (
          WHERE f.units_sold IS NOT NULL
            AND f.time_of_sale >= $6::timestamptz
            AND f.time_of_sale < $5::timestamptz
        ),
        0
      )::numeric > 0::numeric
      ORDER BY gross_profit_total DESC NULLS LAST, product_sku ASC
    `

  const { rows } = await pgConnection.query(query, params)
  return {
    fromDate,
    toDate,
    organisationId,
    location: locationScope.location,
    productSku: productSku || null,
    rows: rows.map((row) => {
      const revenueTotal = Number(row.revenue_total || 0)
      const grossProfitTotal = toNumberOrNull(row.gross_profit_total)
      const currentAverageDailyGrossProfit = divideOrNull(row.current_month_gross_profit_total, currentMonthDays)
      const previous3MonthsAverageDailyGrossProfit = divideOrNull(row.previous_3_months_gross_profit_total, previous3MonthsPeriodDays)

      return {
        productSku: row.product_sku,
        productName: row.product_name,
        totalRows: Number(row.total_rows || 0),
        costedRows: Number(row.costed_rows || 0),
        missingPriceOnlyRows: Number(row.missing_price_only_rows || 0),
        missingCostOnlyRows: Number(row.missing_cost_only_rows || 0),
        missingPriceAndCostRows: Number(row.missing_price_and_cost_rows || 0),
        unitsSoldTotal: Number(row.units_sold_total || 0),
        revenueTotal: revenueTotal,
        cogsTotal: toNumberOrNull(row.cogs_total),
        grossProfitTotal: grossProfitTotal,
        grossMarginPercent: grossProfitTotal === null || revenueTotal === 0
          ? null
          : roundTo2((grossProfitTotal / revenueTotal) * 100),
        previous3MonthsAverageDailyGrossProfit: roundOrNull(previous3MonthsAverageDailyGrossProfit),
        currentAverageDailyGrossProfit: roundOrNull(currentAverageDailyGrossProfit),
        grossProfitDeltaPercent: currentAverageDailyGrossProfit === null
          ? null
          : calculateDeltaPct(currentAverageDailyGrossProfit, previous3MonthsAverageDailyGrossProfit)
      }
    })
  }
}

async function getGrossProfitByLocationTable ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds, aggregationLevel }) {
  checkForRequirements(pgConnection, location)
  const { fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
  const { currentMonthDays, previous3MonthsPeriodDays, params, whereClauses } = buildIndicatorQueryContext({
    organisationId,
    fromDateUtc,
    toDateUtc,
    toDateExclusiveUtc,
    locationScope,
    productSku,
    serviceIds
  })
  const grouping = getLocationTableGrouping(locationScope.location, aggregationLevel)

  const query = `
      WITH filtered AS (
        SELECT
          ${grouping.scopeExpression} AS location_fsid,
          si.time_of_sale,
          si.units_sold::numeric AS units_sold,
          si.unit_price_at_sale,
          si.unit_cost_at_sale
        FROM analytics.sales_indicators si
        INNER JOIN avocado.data_location dl
          ON dl.uuid = si.location_id
        WHERE organisation_id = $1
          AND si.time_of_sale >= $2::timestamptz
          AND si.time_of_sale < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        location_fsid,
        COUNT(*) FILTER (
          WHERE time_of_sale >= $6::timestamptz
            AND time_of_sale < $5::timestamptz
        )::int AS total_rows,
        COUNT(*) FILTER (
          WHERE ${PRICED_AND_COSTED_ROW_CONDITION}
            AND time_of_sale >= $6::timestamptz
            AND time_of_sale < $5::timestamptz
        )::int AS costed_rows,
        COUNT(*) FILTER (
          WHERE unit_price_at_sale IS NULL
            AND unit_cost_at_sale IS NOT NULL
            AND time_of_sale >= $6::timestamptz
            AND time_of_sale < $5::timestamptz
        )::int AS missing_price_only_rows,
        COUNT(*) FILTER (
          WHERE unit_cost_at_sale IS NULL
            AND unit_price_at_sale IS NOT NULL
            AND time_of_sale >= $6::timestamptz
            AND time_of_sale < $5::timestamptz
        )::int AS missing_cost_only_rows,
        COUNT(*) FILTER (
          WHERE unit_price_at_sale IS NULL
            AND unit_cost_at_sale IS NULL
            AND time_of_sale >= $6::timestamptz
            AND time_of_sale < $5::timestamptz
        )::int AS missing_price_and_cost_rows,
        COALESCE(
          SUM(units_sold) FILTER (
            WHERE units_sold IS NOT NULL
              AND time_of_sale >= $6::timestamptz
              AND time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS units_sold_total,
        COALESCE(
          SUM(units_sold * unit_price_at_sale) FILTER (
            WHERE ${PRICED_ROW_CONDITION}
              AND time_of_sale >= $6::timestamptz
              AND time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS revenue_total,
        COALESCE(
          SUM(units_sold * unit_cost_at_sale) FILTER (
            WHERE ${PRICED_AND_COSTED_ROW_CONDITION}
              AND time_of_sale >= $6::timestamptz
              AND time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS cogs_total,
        (
          COALESCE(
            SUM(units_sold * unit_price_at_sale) FILTER (
              WHERE ${PRICED_ROW_CONDITION}
                AND time_of_sale >= $6::timestamptz
                AND time_of_sale < $5::timestamptz
            ),
            0
          )
          - COALESCE(
            SUM(units_sold * unit_cost_at_sale) FILTER (
              WHERE ${PRICED_AND_COSTED_ROW_CONDITION}
                AND time_of_sale >= $6::timestamptz
                AND time_of_sale < $5::timestamptz
            ),
            0
          )
        )::numeric AS gross_profit_total,
        (
          COALESCE(
            SUM(units_sold * unit_price_at_sale) FILTER (
              WHERE ${PRICED_ROW_CONDITION}
                AND time_of_sale >= $4::timestamptz
                AND time_of_sale < $5::timestamptz
            ),
            0
          )
          - COALESCE(
            SUM(units_sold * unit_cost_at_sale) FILTER (
              WHERE ${PRICED_AND_COSTED_ROW_CONDITION}
                AND time_of_sale >= $4::timestamptz
                AND time_of_sale < $5::timestamptz
            ),
            0
          )
        )::numeric AS current_month_gross_profit_total,
        (
          COALESCE(
            SUM(units_sold * unit_price_at_sale) FILTER (
              WHERE ${PRICED_ROW_CONDITION}
                AND time_of_sale >= $2::timestamptz
                AND time_of_sale < $4::timestamptz
            ),
            0
          )
          - COALESCE(
            SUM(units_sold * unit_cost_at_sale) FILTER (
              WHERE ${PRICED_AND_COSTED_ROW_CONDITION}
                AND time_of_sale >= $2::timestamptz
                AND time_of_sale < $4::timestamptz
            ),
            0
          )
        )::numeric AS previous_3_months_gross_profit_total
      FROM filtered
      WHERE location_fsid IS NOT NULL
      GROUP BY location_fsid
      ORDER BY gross_profit_total DESC, location_fsid ASC
    `

  const { rows } = await pgConnection.query(query, params)
  const groupedRows = locationScope.scopeType === 'national'
    ? aggregateNationalLocationRows(rows, aggregationLevel, {
      sumFields: GROSS_PROFIT_LOCATION_SUM_FIELDS,
      sortField: 'gross_profit_total'
    })
    : rows

  return {
    fromDate,
    toDate,
    organisationId,
    location: locationScope.location,
    productSku: productSku || null,
    rows: groupedRows.map((row) => {
      const revenueTotal = Number(row.revenue_total || 0)
      const grossProfitTotal = Number(row.gross_profit_total || 0)
      const currentAverageDailyGrossProfit = Number(row.current_month_gross_profit_total || 0) / currentMonthDays
      const previous3MonthsAverageDailyGrossProfit = Number(row.previous_3_months_gross_profit_total || 0) / previous3MonthsPeriodDays

      return {
        locationFsid: row.location_fsid,
        totalRows: Number(row.total_rows || 0),
        costedRows: Number(row.costed_rows || 0),
        missingPriceOnlyRows: Number(row.missing_price_only_rows || 0),
        missingCostOnlyRows: Number(row.missing_cost_only_rows || 0),
        missingPriceAndCostRows: Number(row.missing_price_and_cost_rows || 0),
        unitsSoldTotal: Number(row.units_sold_total || 0),
        revenueTotal: revenueTotal,
        cogsTotal: Number(row.cogs_total || 0),
        grossProfitTotal: grossProfitTotal,
        grossMarginPercent: revenueTotal === 0 ? null : roundTo2((grossProfitTotal / revenueTotal) * 100),
        previous3MonthsAverageDailyGrossProfit: roundTo2(previous3MonthsAverageDailyGrossProfit),
        currentAverageDailyGrossProfit: roundTo2(currentAverageDailyGrossProfit),
        grossProfitDeltaPercent: calculateDeltaPct(currentAverageDailyGrossProfit, previous3MonthsAverageDailyGrossProfit)
      }
    })
  }
}

async function getGrossProfitMissingPricesSummary ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds }) {
  checkForRequirements(pgConnection, location)
  const { fromDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
  const params = [
    organisationId,
    fromDateUtc.toISOString(),
    toDateExclusiveUtc.toISOString()
  ]
  const whereClauses = []
  appendScopeAndProductFilters({
    params,
    whereClauses,
    locationScope,
    productSku,
    serviceIds
  })

  const summaryQuery = `
      WITH filtered AS (
        SELECT
          si.location_id,
          si.product_sku_id,
          si.units_sold,
          si.unit_price_at_sale,
          si.unit_cost_at_sale
        FROM analytics.sales_indicators si
        INNER JOIN avocado.data_location dl
          ON dl.uuid = si.location_id
        WHERE organisation_id = $1
          AND si.time_of_sale >= $2::timestamptz
          AND si.time_of_sale < $3::timestamptz
          ${whereClauses.join('\n')}
      ),
      sold AS (
        SELECT *
        FROM filtered
        WHERE product_sku_id IS NOT NULL
          AND COALESCE(units_sold, 0) > 0
      )
      SELECT
        COUNT(DISTINCT product_sku_id)::int AS total_sku_count,
        COUNT(DISTINCT product_sku_id) FILTER (
          WHERE unit_price_at_sale IS NULL OR unit_cost_at_sale IS NULL
        )::int AS missing_sku_count,
        COUNT(DISTINCT product_sku_id) FILTER (
          WHERE unit_price_at_sale IS NULL
        )::int AS missing_sell_price_sku_count,
        COUNT(DISTINCT product_sku_id) FILTER (
          WHERE unit_cost_at_sale IS NULL
        )::int AS missing_buy_price_sku_count,
        COUNT(DISTINCT location_id) FILTER (
          WHERE location_id IS NOT NULL
            AND (unit_price_at_sale IS NULL OR unit_cost_at_sale IS NULL)
        )::int AS facilities_with_missing_prices_count
      FROM sold
  `

  const facilitiesQuery = `
      WITH missing_sku_per_facility AS (
        SELECT
          si.location_id,
          si.product_sku_id,
          bool_or(si.unit_price_at_sale IS NULL) AS missing_sell_price,
          bool_or(si.unit_cost_at_sale IS NULL) AS missing_buy_price
        FROM analytics.sales_indicators si
        INNER JOIN avocado.data_location dl
          ON dl.uuid = si.location_id
        WHERE organisation_id = $1
          AND si.time_of_sale >= $2::timestamptz
          AND si.time_of_sale < $3::timestamptz
          AND COALESCE(si.units_sold, 0) > 0
          AND si.product_sku_id IS NOT NULL
          ${whereClauses.join('\n')}
        GROUP BY si.location_id, si.product_sku_id
        HAVING bool_or(si.unit_price_at_sale IS NULL) OR bool_or(si.unit_cost_at_sale IS NULL)
      ),
      expanded AS (
        SELECT
          dl.fsid AS location_fsid,
          dl.name_of_pharmacy AS location_name,
          dl.email AS contact_email,
          COALESCE(dl.owners_phone_number, dl.alt_phone_number) AS contact_number,
          m.product_sku_id AS product_sku,
          COALESCE(ps.display_name, m.product_sku_id) AS product_name,
          m.missing_sell_price,
          m.missing_buy_price
        FROM missing_sku_per_facility m
        INNER JOIN avocado.data_location dl
          ON dl.uuid = m.location_id
        LEFT JOIN avocado.data_productsku ps
          ON ps.old_sku_id = m.product_sku_id
      )
      SELECT
        location_fsid,
        location_name,
        contact_email,
        contact_number,
        json_agg(
          json_build_object(
            'productSku', product_sku,
            'productName', product_name,
            'missingSellPrice', missing_sell_price,
            'missingBuyPrice', missing_buy_price
          )
          ORDER BY product_name ASC, product_sku ASC
        ) AS missing_products
      FROM expanded
      GROUP BY location_fsid, location_name, contact_email, contact_number
      ORDER BY location_name ASC NULLS LAST, location_fsid ASC
  `

  const [{ rows: [summary = {}] }, { rows: facilitiesRows }] = await Promise.all([
    pgConnection.query(summaryQuery, params),
    pgConnection.query(facilitiesQuery, params)
  ])

  const totalSkuCount = Number(summary.total_sku_count || 0)
  const missingSkuCount = Number(summary.missing_sku_count || 0)

  return {
    fromDate: fromDate,
    toDate: toDate,
    organisationId: organisationId,
    location: locationScope.location,
    missingSkuCount: missingSkuCount,
    missingSellPriceSkuCount: Number(summary.missing_sell_price_sku_count || 0),
    missingBuyPriceSkuCount: Number(summary.missing_buy_price_sku_count || 0),
    totalSkuCount: totalSkuCount,
    missingSkuPercent: totalSkuCount > 0 ? (missingSkuCount / totalSkuCount) : null,
    facilitiesWithMissingPricesCount: Number(summary.facilities_with_missing_prices_count || 0),
    facilities: facilitiesRows.map((row) => ({
      locationFsid: row.location_fsid,
      locationName: row.location_name,
      contactEmail: row.contact_email,
      contactNumber: row.contact_number,
      missingProducts: Array.isArray(row.missing_products) ? row.missing_products : []
    }))
  }
}

async function getMonthlyGrossMargin ({ pgConnection, toDate, organisationId, location, productSku, serviceIds }) {
  checkForRequirements(pgConnection, location)
  const { fromDate, fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope } = buildMonthlyIndicatorDateWindow({ toDate, location })
  const { params, whereClauses } = buildMonthlyIndicatorQueryContext({
    organisationId,
    fromDateUtc,
    toDateExclusiveUtc,
    locationScope,
    productSku,
    serviceIds
  })

  const query = `
      WITH months AS (
        SELECT generate_series(
          date_trunc('month', $2::timestamptz),
          date_trunc('month', $3::timestamptz - interval '1 day'),
          interval '1 month'
        ) AS month_start
      ),
      filtered AS (
        SELECT
          si.time_of_sale,
          si.units_sold::numeric AS units_sold,
          si.unit_price_at_sale,
          si.unit_cost_at_sale
        FROM analytics.sales_indicators si
        INNER JOIN avocado.data_location dl
          ON dl.uuid = si.location_id
        WHERE organisation_id = $1
          AND si.time_of_sale >= $2::timestamptz
          AND si.time_of_sale < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        to_char(months.month_start, 'YYYY-MM') AS month,
        COALESCE(
          SUM(filtered.units_sold * filtered.unit_price_at_sale) FILTER (
            WHERE ${PRICED_ROW_CONDITION}
          ),
          0
        )::numeric AS revenue_total,
        COALESCE(
          SUM(filtered.units_sold * filtered.unit_cost_at_sale) FILTER (
            WHERE ${PRICED_AND_COSTED_ROW_CONDITION}
          ),
          0
        )::numeric AS cogs_total
      FROM months
      LEFT JOIN filtered
        ON filtered.time_of_sale >= months.month_start
        AND filtered.time_of_sale < months.month_start + interval '1 month'
      GROUP BY months.month_start
      ORDER BY months.month_start ASC
    `

  const { rows } = await pgConnection.query(query, params)
  const currentMonthKey = getCurrentUtcMonthKey()
  const rowsWithTrend = mapMonthlyGrossMarginRows(rows, currentMonthKey)

  return {
    fromDate: fromDate,
    toDate: toDateUtc.toISOString().slice(0, 10),
    organisationId: organisationId,
    location: locationScope.location,
    productSku: productSku || null,
    rows: rowsWithTrend.slice(-MONTHS_IN_RESPONSE)
  }
}

function buildGrossProfitScopedContext ({ organisationId, fromDate, toDate, location, productSku, serviceIds }) {
  const { fromDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
  const params = [organisationId, fromDateUtc.toISOString(), toDateExclusiveUtc.toISOString()]
  const whereClauses = []

  appendScopeAndProductFilters({ params, whereClauses, locationScope, productSku, serviceIds })

  return { locationScope, params, whereClauses }
}

function grossProfitTotalOrNull (row) {
  return Number(row.costed_rows || 0) === 0 ? null : Number(row.gross_profit_total || 0)
}

function buildGrossProfitTotalQuery ({ groupByExpression, whereClauses = [] } = {}) {
  return `
    WITH filtered AS (
      SELECT
        ${groupByExpression ? groupByExpression + ' AS group_key,' : ''}
        si.units_sold::numeric AS units_sold,
        si.unit_price_at_sale,
        si.unit_cost_at_sale
      FROM analytics.sales_indicators si
      INNER JOIN avocado.data_location dl
        ON dl.uuid = si.location_id
      WHERE si.organisation_id = $1
        AND si.time_of_sale >= $2::timestamptz
        AND si.time_of_sale < $3::timestamptz
        ${whereClauses.join('\n')}
    )
    SELECT
      ${groupByExpression ? 'group_key,' : ''}
      COUNT(*) FILTER (WHERE ${PRICED_AND_COSTED_ROW_CONDITION})::int AS costed_rows,
      (
        COALESCE(
          SUM(units_sold * unit_price_at_sale) FILTER (WHERE ${PRICED_ROW_CONDITION}),
          0
        )
        -
        COALESCE(
          SUM(units_sold * unit_cost_at_sale) FILTER (WHERE ${PRICED_AND_COSTED_ROW_CONDITION}),
          0
        )
      )::numeric AS gross_profit_total
    FROM filtered
    ${groupByExpression ? 'GROUP BY group_key' : ''}
  `
}

async function getGrossProfitTotal ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds }) {
  checkForRequirements(pgConnection, location)
  const { params, whereClauses } = buildGrossProfitScopedContext({ organisationId, fromDate, toDate, location, productSku, serviceIds })

  const query = buildGrossProfitTotalQuery({ whereClauses })

  const { rows } = await pgConnection.query(query, params)
  const row = rows[0] || {}
  return Number(row.gross_profit_total || 0)
}

async function getGrossProfitTotalsByProduct ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds }) {
  checkForRequirements(pgConnection, location)
  const { params, whereClauses } = buildGrossProfitScopedContext({ organisationId, fromDate, toDate, location, productSku, serviceIds })

  const query = buildGrossProfitTotalQuery({ groupByExpression: 'si.product_sku_id', whereClauses })

  const { rows } = await pgConnection.query(query, params)
  const totalsBySku = new Map()
  rows.forEach((row) => {
    totalsBySku.set(row.group_key, grossProfitTotalOrNull(row))
  })
  return totalsBySku
}

async function getGrossProfitTotalsByLocation ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds, aggregationLevel }) {
  checkForRequirements(pgConnection, location)
  const { locationScope, params, whereClauses } = buildGrossProfitScopedContext({ organisationId, fromDate, toDate, location, productSku, serviceIds })
  const grouping = getLocationTableGrouping(locationScope.location, aggregationLevel)

  const query = buildGrossProfitTotalQuery({ groupByExpression: grouping.scopeExpression, whereClauses })

  const { rows } = await pgConnection.query(query, params)
  const locationRows = rows
    .filter((row) => row.group_key)
    .map((row) => ({ location_fsid: row.group_key, costed_rows: row.costed_rows, gross_profit_total: row.gross_profit_total }))

  const groupedRows = locationScope.scopeType === 'national'
    ? aggregateNationalLocationRows(locationRows, aggregationLevel, {
      sumFields: ['costed_rows', 'gross_profit_total'],
      sortField: 'gross_profit_total'
    })
    : locationRows

  const totalsByLocation = new Map()
  groupedRows.forEach((row) => {
    totalsByLocation.set(row.location_fsid, grossProfitTotalOrNull(row))
  })
  return totalsByLocation
}

module.exports = {
  getGrossProfitSummary,
  getGrossProfitByProductTable,
  getGrossProfitByLocationTable,
  getGrossProfitMissingPricesSummary,
  getMonthlyGrossMargin,
  getGrossProfitTotal,
  getGrossProfitTotalsByProduct,
  getGrossProfitTotalsByLocation
}
